import type { Embedding } from '@shared/utils/search';
import { scoreAndFilterByEmbedding, usePrecomputedEmbeddings } from '@shared/utils/search';
import type { FuseOptions, FuseResultWithScore } from 'fuse.js';
import type Fuse from 'fuse.js';
import { useMemo } from 'react';
import type { StationeryTemplate } from '../../DesignsGallery.types';
import { useValuePropsPayload } from '../../useValuePropsPayload';
import type { GalleryTemplate, GalleryValueProp } from '../GalleryItem/GalleryItem';
import { SIMILARITY_THRESHOLD } from '@shared/utils/search/scoreAndFilterByEmbedding';
import { useSearchContext } from './SearchContext';

function getThemeIdOrEquivalent(item: GalleryTemplate | GalleryValueProp) {
  if (item.type === 'prop') return `valueprop_${item.telemetryId ?? ''}`;
  return item.themeId;
}

export function useSearchFilteredGalleryItems(templates: ReadonlyArray<GalleryTemplate>, fuse: Fuse<StationeryTemplate, FuseOptions<StationeryTemplate>>) {
  const { themes, embeddingMap, wellKnownEmbeddings } = usePrecomputedEmbeddings();
  const { activeTerm: term, activeTermEmbedding: termEmbedding, wantsFree } = useSearchContext();
  const isActive = !!termEmbedding;
  const valuePropsData = useValuePropsPayload();

  const filteredItems = useMemo(() => {
    if (!themes) return templates; // Means still loading
    if (!isActive) return templates;

    /** Injects extra customs for well known tags. */
    const getItemCustomsForGalleryItem = (item: GalleryTemplate | GalleryValueProp): ReadonlyArray<Embedding> => {
      if (item.type !== 'template') return [];

      const itemCustoms: Embedding[] = [];

      if (item.tags.includes('photo')) itemCustoms.push(wellKnownEmbeddings.photo);
      if (item.tags.includes('qrCode')) itemCustoms.push(wellKnownEmbeddings.qrCode);

      return itemCustoms;
    };

    const valueProps: ReadonlyArray<GalleryValueProp> = (valuePropsData?.props ?? []).map(prop => ({
      ...prop,
      type: 'prop',
    }));
    const items = [...templates, ...valueProps];

    const fuseResults = fuse.search(term) as FuseResultWithScore<StationeryTemplate>[];

    return scoreAndFilterByEmbedding(items, {
      termEmbedding,
      themes,
      embeddingMap,
      getThemeId: getThemeIdOrEquivalent,
      fuseResults,
      getFuseItemThemeId: item => item.themeId,
      threshold: SIMILARITY_THRESHOLD,
      wantsFree,
      isFreeItem: item => item.type === 'template' && (item.mappedTags?.includes('price:basic') ?? false),
      getItemCustoms: getItemCustomsForGalleryItem,
    });
  }, [templates, valuePropsData, fuse, themes, embeddingMap, term, termEmbedding, isActive, wantsFree, wellKnownEmbeddings]);

  return {
    isActive,
    filteredItems,
  };
}
